/**
 * MasterSnsSignupPage — A-3-6. SNS 가입 사업자 인증 (사장님)
 * 진입: SnsRoleSelectPage → role=master → /signup/sns/master (route state)
 *
 * 전화번호 로직:
 *   blur(10자리+) → GET /member/users/exists?phone=
 *     exists=false  → 녹색 표시 / 폼 제출 → POST /member/join/sns
 *     exists=true   → 노란 경고 박스 + [인증번호 받기]
 *                      → POST /member/auth/phone-verify/request
 *                      → OTP 입력 → POST /member/auth/phone-verify/verify → verifyToken
 *                      → 폼 제출 → POST /member/auth/sns-verify
 *                           LINKED       → POST /gatepass/social/login-with-token → setAuth → /app
 *                           ALREADY_LINKED → 에러 표시
 */
import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@indipia/shared';
import { checkUserExists, requestPhoneVerify, verifyPhoneCode, snsVerify } from '../api/member-auth';
import { joinSns, verifyBusinessNumber } from '../api/member-join';
import { validateReferralCode } from '../api/referral';
import { loginWithSnsToken } from '../api/social-auth';
import { applyLoginTokens } from '../lib/post-login';
import { AddressSearchField, AddressSelectResult } from '../components/AddressSearchField';
import { TermsConsentSection, INITIAL_TERMS_CONSENT, type TermsConsentValue } from '../components/TermsConsentSection';
import { PhoneOtpVerify, PhoneOtpSendButton } from '../components/PhoneOtpVerify';
import { REFERRAL_CODE_STORAGE_KEY } from './ReferralLandingPage';

// C-8(추천인 초대) — MasterSignupPage.tsx의 peekPendingReferralCode/clearPendingReferralCode와 동일
// 용도(별도 파일로 뽑을 만큼 크지 않아 각 가입 화면에 그대로 복제).
function peekPendingReferralCode(): string | undefined {
  try {
    return localStorage.getItem(REFERRAL_CODE_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function clearPendingReferralCode() {
  try {
    localStorage.removeItem(REFERRAL_CODE_STORAGE_KEY);
  } catch {
    // 저장소 접근 불가 — 무시
  }
}

interface SnsRouteState {
  provider:     string;
  socialId:     string;
  email:        string | null;
  socialName:   string | null;
  mobile:       string | null;
  snsLinkToken: string;
}

// ── 공용 인풋 스타일 ──────────────────────────────────────────────────────

function inpStyle(overrides?: React.CSSProperties): React.CSSProperties {
  return {
    width: '100%', padding: '13px 16px',
    background: 'var(--shell-bg)', border: '1px solid var(--shell-border)',
    borderRadius: 'var(--radius-md)', color: 'var(--shell-on-surface)',
    outline: 'none', fontFamily: 'inherit',
    transition: 'border-color .15s, box-shadow .15s',
    ...overrides,
  };
}
const INPUT_CLASS = 'text-body-sm';

const labelStyle: React.CSSProperties = { marginBottom: 6, display: 'block' };
const LABEL_CLASS = 'text-caption font-semibold text-text';

/** 인디고 브랜드 면(shell.point) 위 흰 계열 — hex·rgba 없이 토큰으로 표현 */
const onPoint = (pct: number) => `color-mix(in srgb, var(--gt-on-primary) ${pct}%, transparent)`;

/** 표준 .input 포커스/블러 — border + ring (D-09 §15.2) */
const focusInp = (el: HTMLInputElement) => {
  el.style.borderColor = 'var(--gt-primary)';
  el.style.boxShadow = '0 0 0 3px var(--gt-subtle)';
};
const blurInp = (el: HTMLInputElement, errored = false) => {
  el.style.borderColor = errored ? 'var(--color-error)' : 'var(--shell-border)';
  el.style.boxShadow = 'none';
};


// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────

export function MasterSnsSignupPage() {
  const navigate    = useNavigate();
  const location    = useLocation();
  const queryClient = useQueryClient();
  const setAuth     = useAuthStore((s) => s.setAuth);
  const sns         = (location.state ?? {}) as Partial<SnsRouteState>;

  // ── 전화번호 포맷 (010-XXXX-XXXX) ─────────────────────────────────────
  const formatPhone = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 11);
    if (d.length < 4) return d;
    if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
    return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  };

  // ── 폼 상태 ────────────────────────────────────────────────────────────
  const [compName,       setCompName]       = useState('');
  const [compRepName,     setCompRepName]     = useState(sns.socialName ?? '');
  const [compAddress,    setCompAddress]    = useState('');
  const [compAddressDtl, setCompAddressDtl] = useState('');
  const [compGpsLat,     setCompGpsLat]     = useState<number | null>(null);
  const [compGpsLng,     setCompGpsLng]     = useState<number | null>(null);
  const [mobile,         setMobile]         = useState(formatPhone(sns.mobile ?? ''));
  const [email,          setEmail]          = useState(sns.email ?? '');
  const [compNo,       setCompNo]       = useState('');

  // C-8(추천인 초대) — 링크로 들어왔으면 자동 입력, 직접 입력·수정도 가능.
  const [referralCode,     setReferralCode]     = useState(() => peekPendingReferralCode() ?? '');
  const [referralChecking, setReferralChecking] = useState(false);
  const [referralMsg,      setReferralMsg]      = useState<{ text: string; ok: boolean } | null>(null);
  // 마지막으로 요청을 건 코드 — blur를 빠르게 연타하면 먼저 보낸 요청이 나중에 응답할 수 있어
  // (네트워크 지연 역전), 응답이 왔을 때 낡은 응답인지 확인하는 용도.
  const lastReferralCheckRef = useRef('');
  const checkReferralCode = async (code: string) => {
    const trimmed = code.trim();
    if (!trimmed) {
      // ref도 같이 비운다 — 안 그러면 앞서 날아간(비어있지 않았던) 요청이 나중에 응답할 때
      // "그사이 안 바뀜"으로 오판해 방금 비운 입력창에 낡은 결과를 다시 채워넣는다.
      lastReferralCheckRef.current = '';
      setReferralMsg(null);
      return;
    }
    lastReferralCheckRef.current = trimmed;
    setReferralChecking(true);
    try {
      const result = await validateReferralCode(trimmed);
      if (lastReferralCheckRef.current !== trimmed) return;
      setReferralMsg(result.valid
        ? { text: `${result.referrerName ?? ''} 사장님이 추천했어요`, ok: true }
        : { text: '코드를 찾을 수 없어요. 그래도 가입은 계속 진행돼요.', ok: false });
    } catch {
      if (lastReferralCheckRef.current !== trimmed) return;
      setReferralMsg(null);
    } finally {
      if (lastReferralCheckRef.current === trimmed) setReferralChecking(false);
    }
  };
  useEffect(() => {
    if (referralCode.trim()) void checkReferralCode(referralCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── SNS가 전화번호를 제공했는지 여부 — 제공된 번호는 이름처럼 수정 불가(신뢰), 미제공 시 본인이 입력 후 인증 ──
  const hasSnsMobile = Boolean(sns.mobile);

  // ── 전화번호 중복 체크 상태 ─────────────────────────────────────────────
  const [phoneCheckDone,    setPhoneCheckDone]    = useState(false);
  const [phoneHasDuplicate, setPhoneHasDuplicate] = useState(false);
  const [phoneCheckLoading, setPhoneCheckLoading] = useState(false);
  const lastCheckedPhone = useRef('');

  // ── OTP 상태 ───────────────────────────────────────────────────────────
  const [otpSent,     setOtpSent]     = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading,  setOtpLoading]  = useState(false);
  const [otpError,    setOtpError]    = useState<string | null>(null);

  // ── 사업자 조회 상태 ────────────────────────────────────────────────────
  const [bizVerified,  setBizVerified]  = useState(false);
  const [bizStatusMsg, setBizStatusMsg] = useState<{ text: string; ok: boolean } | null>(null);
  const [bizLoading,   setBizLoading]   = useState(false);

  // ── 제출 상태 ──────────────────────────────────────────────────────────
  const [isLoading,   setIsLoading]   = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [terms,       setTerms]       = useState<TermsConsentValue>(INITIAL_TERMS_CONSENT);

  // ── 검증 상태 (blur/제출 시 표시) ────────────────────────────────────────
  const [mobileTouched,      setMobileTouched]      = useState(false);
  const [otpTouched,         setOtpTouched]         = useState(false);
  const [emailTouched,       setEmailTouched]       = useState(false);
  const [compNameTouched,    setCompNameTouched]    = useState(false);
  const [compRepNameTouched,  setCompRepNameTouched]  = useState(false);
  const [compAddressTouched, setCompAddressTouched] = useState(false);
  const [termsTouched,       setTermsTouched]       = useState(false);
  const mobileDigitsForErr  = mobile.replace(/\D/g, '');
  const mobileIsMobileFormat = mobileDigitsForErr.startsWith('010') || mobileDigitsForErr.startsWith('999'); // 999=테스트 목업 번호
  const mobileErr      = mobileTouched && mobileDigitsForErr.length >= 10 && !mobileIsMobileFormat ? '010으로 시작하는 휴대폰 번호를 입력해 주세요.'
    : mobileTouched && mobileDigitsForErr.length < 10 ? '전화번호를 정확히 입력해 주세요.'
    : null;
  const otpErr         = otpTouched && phoneCheckDone && (phoneHasDuplicate || !hasSnsMobile) && !otpVerified ? '전화번호 인증을 완료해 주세요.' : null;
  const emailErr       = emailTouched       && !email.includes('@')          ? '이메일 형식이 올바르지 않아요.'   : null;
  const compNameErr    = compNameTouched    && !compName.trim()              ? '상호명을 입력해 주세요.'          : null;
  const compRepNameErr  = compRepNameTouched  && !compRepName.trim()            ? '대표자명을 입력해 주세요.'        : null;
  const compAddressErr = compAddressTouched && !compAddress                  ? '사업자 주소를 입력해 주세요.'     : null;
  const termsErr       = termsTouched       && !terms.terms ? '필수 약관에 동의해 주세요.' : null;

  const handleMobileChange = (v: string) => {
    const formatted = formatPhone(v);
    setMobile(formatted);
    // 번호 변경 시 체크 초기화
    const digits = formatted.replace(/\D/g, '');
    if (digits !== lastCheckedPhone.current.replace(/\D/g, '')) {
      setPhoneCheckDone(false);
      setPhoneHasDuplicate(false);
      setOtpSent(false);
      setOtpVerified(false);
      setOtpError(null);
    }
  };

  // ── 전화번호 blur → 중복 체크 ──────────────────────────────────────────
  const handlePhoneBlur = async () => {
    setMobileTouched(true);
    const digits = mobile.replace(/\D/g, '');
    if (digits.length < 10) return;
    if (digits === lastCheckedPhone.current.replace(/\D/g, '') && phoneCheckDone) return;

    setPhoneCheckLoading(true);
    try {
      const result = await checkUserExists(mobile);
      lastCheckedPhone.current = mobile;
      setPhoneHasDuplicate(result.exists);
      setPhoneCheckDone(true);
      setOtpSent(false);
      setOtpVerified(false);
      setOtpError(null);
    } catch {
      // 체크 실패 시 조용히 무시 (제출 시 서버에서 검증)
    } finally {
      setPhoneCheckLoading(false);
    }
  };

  // ── SNS가 번호를 제공한 경우 — 신뢰하고 잠그되, 중복 계정 연동 여부는 확인해야 함 ──
  useEffect(() => {
    if (!hasSnsMobile) return;
    void handlePhoneBlur();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── OTP 발송 ───────────────────────────────────────────────────────────
  const handleOtpRequest = async () => {
    setOtpLoading(true);
    setOtpError(null);
    try {
      await requestPhoneVerify(mobile);
      setOtpSent(true);
    } catch {
      setOtpError('인증번호 발송에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setOtpLoading(false);
    }
  };

  // ── OTP 확인 → sns-verify 즉시 호출 ──────────────────────────────────
  const handleOtpVerify = async (code: string) => {
    setOtpLoading(true);
    setOtpError(null);
    try {
      const otpResult = await verifyPhoneCode(mobile, code);
      if (!otpResult.verified || !otpResult.verifyToken) {
        setOtpError('인증번호가 일치하지 않습니다.');
        return;
      }
      // OTP 성공 → SNS 연동 여부 즉시 확인
      const snsResult = await snsVerify({
        snsType:      (sns.provider ?? '').toUpperCase(),
        snsSubjectId: sns.socialId  ?? '',
        mobile,
        verifyToken:  otpResult.verifyToken,
        email,
        name: sns.socialName ?? '',
      });
      if (snsResult.status === 'LINKED') {
        // 이미 연동된 SNS → 버튼 없이 바로 로그인
        const tokens = await loginWithSnsToken(sns.snsLinkToken ?? '');
        applyLoginTokens(tokens, setAuth, queryClient);
        void navigate('/app', { replace: true });
        return;
      } else if (snsResult.status === 'ALREADY_LINKED') {
        setOtpError('이미 다른 계정에 연동된 SNS입니다.');
        return;
      }
      setOtpVerified(true);
    } catch {
      setOtpError('인증번호 확인에 실패했습니다. 다시 시도해 주세요.');
    } finally {
      setOtpLoading(false);
    }
  };

  // ── 국세청 사업자 조회 ─────────────────────────────────────────────────
  const handleBizVerify = async () => {
    const raw = compNo.replace(/-/g, '');
    if (raw.length < 10) {
      setBizStatusMsg({ text: '사업자등록번호 10자리를 입력해 주세요.', ok: false });
      return;
    }
    setBizLoading(true);
    setBizStatusMsg(null);
    setBizVerified(false);
    try {
      const result = await verifyBusinessNumber(raw);
      if (result.bizStatus === 'VALID_NEW') {
        setBizVerified(true);
        setBizStatusMsg({ text: '인증되었습니다.', ok: true });
        if (result.companyName && !compName) setCompName(result.companyName);
        if (result.compRepName  && !compRepName) setCompRepName(result.compRepName);
      } else if (result.bizStatus === 'VALID_EXISTING') {
        setBizStatusMsg({ text: '이미 가입된 사업자입니다. 직원으로 가입하거나 로그인해 주세요.', ok: false });
      } else {
        setBizStatusMsg({ text: result.noticeMessage ?? '폐업된 사업자입니다.', ok: false });
      }
    } catch (err) {
      const m = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setBizStatusMsg({ text: m ?? '사업자 조회에 실패했습니다. 다시 시도해 주세요.', ok: false });
    } finally {
      setBizLoading(false);
    }
  };

  // 제출 클릭 시 검증 — 문제 있는 필드에 에러 표시, 통과해야만 제출 진행
  const validateForm = (): boolean => {
    setMobileTouched(true);
    setOtpTouched(true);
    setTermsTouched(true);
    let ok = true;

    if (mobileDigitsForErr.length < 10) ok = false;
    else if (phoneCheckLoading)         ok = false;
    else if (!phoneCheckDone)           { void handlePhoneBlur(); ok = false; }

    if (!terms.terms) ok = false;

    // 이미 가입된 번호(SNS 전용 계정 연동) 흐름 — OTP 인증만 통과하면 됨
    if (phoneHasDuplicate) {
      if (!otpVerified) ok = false;
      return ok;
    }

    // 중복은 아니지만 SNS가 번호를 제공하지 않아 직접 입력한 경우 — 본인 확인 OTP 필수
    if (!hasSnsMobile && !otpVerified) ok = false;

    setCompNameTouched(true);
    setCompRepNameTouched(true);
    setCompAddressTouched(true);
    setEmailTouched(true);
    if (!compName.trim())     ok = false;
    if (!compRepName.trim())   ok = false;
    if (!compAddress)         ok = false;
    if (!email.includes('@')) ok = false;
    if (!bizVerified) {
      const raw = compNo.replace(/-/g, '');
      if (raw.length === 0)      setBizStatusMsg({ text: '사업자등록번호를 입력해 주세요.', ok: false });
      else if (raw.length < 10)  setBizStatusMsg({ text: '사업자등록번호 10자리를 입력해 주세요.', ok: false });
      else                       setBizStatusMsg({ text: '국세청 조회를 통해 사업자 인증을 완료해 주세요.', ok: false });
      ok = false;
    }
    return ok;
  };

  // ── 폼 제출 (joinSns만 담당 — LINKED 케이스는 handleOtpVerify에서 처리) ──
  const handleSubmit = async () => {
    setIsLoading(true);
    setSubmitError(null);
    try {
      await joinSns({
        snsType:            (sns.provider ?? '').toUpperCase(),
        snsSubjectId:       sns.socialId  ?? '',
        memberName:         sns.socialName ?? '',
        email,
        mobile,
        master:             true,
        compNo:              compNo.replace(/-/g, ''),
        compType:            'INDIVIDUAL',
        compName,
        compRepName,
        compPhone:          mobile,
        compEmail:          email,
        compAddress:        compAddress,
        compAddressDtl:     compAddressDtl || undefined,
        compGpsLat:         compGpsLat     ?? undefined,
        compGpsLng:         compGpsLng     ?? undefined,
        marketingConsent:   terms.marketing,
        marketingChannels:  terms.channels,
        nightMarketingConsent: terms.nightAd,
        // C-8(추천인 초대) — 링크로 자동 채워졌든 직접 입력했든, 입력창에 남아있는 값을 그대로 보낸다.
        referralCode:       referralCode.trim() || undefined,
      });
      // 1회성 소비 — joinSns가 성공했을 때만 여기 도달한다(실패 시 catch로 빠져 재시도에도
      // 코드를 그대로 쓸 수 있다).
      clearPendingReferralCode();
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      setSubmitError(err?.response?.data?.message ?? '가입 처리 중 오류가 발생했습니다.');
      setIsLoading(false);
      return;
    }

    // ── 가입 성공 → 자동로그인 (실패해도 "가입 성공"은 유지, 로그인 화면으로 폴백) ──
    try {
      const tokens = await loginWithSnsToken(sns.snsLinkToken ?? '');
      applyLoginTokens(tokens, setAuth, queryClient);
      void navigate('/app', { replace: true });
    } catch {
      void navigate('/login', { replace: true, state: { signupDone: true } });
    } finally {
      setIsLoading(false);
    }
  };

  // ── 렌더 ──────────────────────────────────────────────────────────────

  const providerLabel: Record<string, string> = { kakao: '카카오', naver: '네이버', google: '구글' };
  const pLabel = providerLabel[sns.provider ?? ''] ?? 'SNS';

  return (
    <div className="flex min-h-screen flex-col overflow-hidden bg-surface md:flex-row">

      {/* 좌측 브랜딩 */}
      <div className="relative flex w-full shrink-0 flex-col overflow-hidden bg-primary-700 px-7 pb-9 pt-10 md:w-[36%] md:min-h-screen md:px-11 md:pb-12 md:pt-[52px]">
        <div className="flex items-center gap-2">
          <span className="text-body-sm font-bold uppercase tracking-widest text-white">INDIPIA</span>
          <span className="bg-white/60" style={{ width: 5, height: 5, borderRadius: '50%', flexShrink: 0, display: 'inline-block' }} />
          <span className="text-caption font-semibold uppercase tracking-widest text-[var(--alpha-white-800)]">Pro</span>
        </div>

        {/* SNS 인증 배지 */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: 'var(--alpha-white-200)', border: `1px solid ${onPoint(35)}`,
          borderRadius: 20, padding: '6px 14px', marginTop: 28, marginBottom: 32, alignSelf: 'flex-start',
        }}>
          <span
            className="text-caption font-bold"
            style={{
              width: 22, height: 22, borderRadius: '50%', background: 'var(--brand-kakao-container)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--brand-kakao-label)', flexShrink: 0,
            }}
          >
            {(sns.provider ?? 'K')[0].toUpperCase()}
          </span>
          <span className="text-caption font-semibold text-white">
            {pLabel} 인증 완료
          </span>
        </div>

        <div className="flex flex-1 flex-col justify-center pb-8">
          <p className="text-caption font-semibold uppercase tracking-widest text-[var(--alpha-white-800)]" style={{ marginBottom: 18 }}>SNS Signup</p>
          <h2 className="text-display-sm font-bold text-white" style={{ wordBreak: 'keep-all' }}>
            마지막으로<br />사업자를<br />확인해요.
          </h2>
          <div className="bg-white/60" style={{ width: 40, height: 2, margin: '22px 0', borderRadius: 2 }} />
          <p className="text-body-sm text-[var(--alpha-white-800)]">
            사업자번호 인증 후<br />스튜디오가 바로 시작됩니다.
          </p>
        </div>
        <p className="text-caption text-[var(--alpha-white-800)]">v0.5 · 2026</p>
      </div>

      {/* 우측 폼 */}
      <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto bg-surface-raised px-7 py-9 md:px-[64px] md:py-[52px]">
        <div style={{ width: '100%', maxWidth: 480 }}>

          <h1 className="text-heading-md font-bold text-text" style={{ marginBottom: 8 }}>사업자 인증</h1>
          <p className="text-body-sm text-text-secondary" style={{ marginBottom: 28 }}>
            SNS 인증이 완료되었어요. 사업자 정보를 입력해 주세요.
          </p>

          {/* 이름 — SNS 자동입력 (readonly) */}
          <div style={{ marginBottom: 14 }}>
            <label className={LABEL_CLASS} style={labelStyle}>이름</label>
            <input type="text" readOnly aria-label="이름" value={sns.socialName ?? ''} className={INPUT_CLASS} style={inpStyle({ color: 'var(--shell-on-surface-2)', cursor: 'default', border: '1.5px solid var(--color-success)' })} />
          </div>

          {/* 연락처 — SNS가 제공한 번호는 이름처럼 잠금(신뢰), 미제공 시 직접 입력 후 OTP 인증 */}
          <div style={{ marginBottom: 14 }}>
            <label className={LABEL_CLASS} style={labelStyle}>연락처 <span style={{ color: 'var(--color-error)' }}>*</span></label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="tel" aria-label="연락처" placeholder="010-0000-0000"
                value={mobile}
                readOnly={hasSnsMobile}
                onChange={(e) => { if (!hasSnsMobile) handleMobileChange(e.target.value); }}
                onBlur={() => { if (!hasSnsMobile) void handlePhoneBlur(); }}
                className={INPUT_CLASS}
                style={inpStyle({
                  flex: 1,
                  cursor: hasSnsMobile ? 'default' : undefined,
                  color: hasSnsMobile ? 'var(--shell-on-surface-2)' : undefined,
                  borderColor: mobileErr
                    ? 'var(--color-error)'
                    : phoneCheckDone
                      ? phoneHasDuplicate
                        ? otpVerified ? 'var(--color-success)' : 'var(--color-warning)'
                        : 'var(--color-success)'
                      : 'var(--shell-border)',
                })}
                onFocus={(e) => {
                  if (!hasSnsMobile && !phoneCheckDone && !mobileErr) focusInp(e.target);
                }}
              />
              {phoneCheckDone && !phoneCheckLoading && mobileIsMobileFormat && (phoneHasDuplicate || !hasSnsMobile) && !otpSent && !otpVerified && (
                <PhoneOtpSendButton
                  onClick={() => void handleOtpRequest()}
                  loading={otpLoading}
                  label={phoneHasDuplicate ? '인증번호 받기' : '인증번호 발송'}
                />
              )}
            </div>
            {mobileErr && (
              <p className="text-caption" style={{ marginTop: 5, color: 'var(--color-error)' }}>{mobileErr}</p>
            )}
            {!mobileErr && phoneCheckLoading && (
              <p className="text-caption text-text-secondary" style={{ marginTop: 5 }}>확인 중…</p>
            )}
            {phoneCheckDone && !phoneHasDuplicate && !phoneCheckLoading && hasSnsMobile && (
              <p className="text-caption" style={{ marginTop: 5, color: 'var(--color-success)', display: 'flex', alignItems: 'center', gap: 4 }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                SNS로 인증된 번호예요
              </p>
            )}
            {phoneCheckDone && !phoneHasDuplicate && !phoneCheckLoading && !hasSnsMobile && (
              <PhoneOtpVerify
                otpSent={otpSent}
                otpVerified={otpVerified}
                onVerify={handleOtpVerify}
                loading={otpLoading}
                error={otpError}
                variant="verify"
              />
            )}
            {phoneCheckDone && phoneHasDuplicate && !phoneCheckLoading && (
              <PhoneOtpVerify
                otpSent={otpSent}
                otpVerified={otpVerified}
                onVerify={handleOtpVerify}
                loading={otpLoading}
                error={otpError}
                variant="link"
              />
            )}
            {otpErr && <p className="text-caption" style={{ marginTop: 5, color: 'var(--color-error)' }}>{otpErr}</p>}
          </div>

          {/* 이메일 — SNS 자동입력 (수정 가능) */}
          <div style={{ marginBottom: 20 }}>
            <label className={LABEL_CLASS} style={labelStyle}>이메일 <span style={{ color: 'var(--color-error)' }}>*</span></label>
            <input
              type="email" placeholder="이메일을 입력하세요"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={INPUT_CLASS}
              style={inpStyle({
                borderColor: emailErr ? 'var(--color-error)' : email && email.includes('@') ? 'var(--color-success)' : 'var(--shell-border)',
              })}
              onFocus={(e) => { if (!email.includes('@') && !emailErr) focusInp(e.target); }}
              onBlur={(e) => {
                if (!email.includes('@')) blurInp(e.target);
                setEmailTouched(true);
              }}
            />
            {emailErr && <p className="text-caption" style={{ marginTop: 5, color: 'var(--color-error)' }}>{emailErr}</p>}
          </div>

          <div style={{ height: 1, background: 'var(--shell-border)', margin: '4px 0 18px' }} />

          {/* 사업자등록번호 + 국세청 조회 */}
          <div style={{ marginBottom: 6 }}>
            <label className={LABEL_CLASS} style={labelStyle}>사업자등록번호 <span style={{ color: 'var(--color-error)' }}>*</span></label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type="text" inputMode="numeric" maxLength={12}
                placeholder="000-00-00000"
                value={compNo}
                onChange={(e) => {
                  setBizVerified(false);
                  setBizStatusMsg(null);
                  const d = e.target.value.replace(/\D/g, '').slice(0, 10);
                  const fmt = d.length < 4 ? d : d.length < 6 ? `${d.slice(0,3)}-${d.slice(3)}` : `${d.slice(0,3)}-${d.slice(3,5)}-${d.slice(5)}`;
                  setCompNo(fmt);
                }}
                onBlur={() => {
                  if (!bizVerified) {
                    const raw = compNo.replace(/-/g, '');
                    if (raw.length === 0)     setBizStatusMsg({ text: '사업자등록번호를 입력해 주세요.', ok: false });
                    else if (raw.length < 10) setBizStatusMsg({ text: '사업자등록번호 10자리를 입력해 주세요.', ok: false });
                  }
                }}
                className={INPUT_CLASS}
                style={inpStyle({
                  flex: 1,
                  borderColor: bizVerified ? 'var(--color-success)' : bizStatusMsg && !bizStatusMsg.ok ? 'var(--color-error)' : 'var(--shell-border)',
                })}
              />
              <button
                type="button"
                onClick={handleBizVerify}
                disabled={bizLoading}
                className="text-body-sm font-bold"
                style={{
                  flexShrink: 0, padding: '0 18px', background: 'var(--gt-primary)', border: 'none',
                  borderRadius: 12, color: 'var(--gt-on-primary)',
                  cursor: bizLoading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
                  fontFamily: 'inherit', opacity: bizLoading ? 0.6 : 1, transition: 'filter .15s, opacity .15s',
                }}
                onMouseEnter={(e) => { if (!bizLoading) e.currentTarget.style.filter = 'brightness(.92)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.filter = 'none'; }}
              >
                {bizLoading ? '조회 중…' : '인증'}
              </button>
            </div>
            {bizStatusMsg && (
              <p className="text-caption" style={{ marginTop: 5, color: bizStatusMsg.ok ? 'var(--color-success)' : 'var(--color-error)' }}>
                {bizStatusMsg.text}
              </p>
            )}
            {!bizStatusMsg && (
              <p className="text-caption text-text-secondary" style={{ marginTop: 5 }}>하이픈(-) 없이 10자리 입력</p>
            )}
          </div>

          {/* 상호명 */}
          <div style={{ marginBottom: 14 }}>
            <label className={LABEL_CLASS} style={labelStyle}>상호명 <span style={{ color: 'var(--color-error)' }}>*</span></label>
            <input
              type="text" placeholder="예) 홍길동 인테리어" value={compName} onChange={(e) => setCompName(e.target.value)}
              className={INPUT_CLASS}
              style={inpStyle(compNameErr ? { borderColor: 'var(--color-error)' } : {})}
              onFocus={(e) => { if (!compNameErr) focusInp(e.target); }}
              onBlur={(e) => {
                if (!compNameErr) blurInp(e.target);
                setCompNameTouched(true);
              }}
            />
            {compNameErr && <p className="text-caption" style={{ marginTop: 5, color: 'var(--color-error)' }}>{compNameErr}</p>}
          </div>

          {/* 대표자명 */}
          <div style={{ marginBottom: 14 }}>
            <label className={LABEL_CLASS} style={labelStyle}>대표자명 <span style={{ color: 'var(--color-error)' }}>*</span></label>
            <input
              type="text" placeholder="이름과 동일하면 자동 입력" value={compRepName} onChange={(e) => setCompRepName(e.target.value)}
              className={INPUT_CLASS}
              style={inpStyle(compRepNameErr ? { borderColor: 'var(--color-error)' } : {})}
              onFocus={(e) => { if (!compRepNameErr) focusInp(e.target); }}
              onBlur={(e) => {
                if (!compRepNameErr) blurInp(e.target);
                setCompRepNameTouched(true);
              }}
            />
            {compRepNameErr && <p className="text-caption" style={{ marginTop: 5, color: 'var(--color-error)' }}>{compRepNameErr}</p>}
          </div>

          {/* 사업자 주소 */}
          <div style={{ marginBottom: compAddress ? 10 : 14 }}>
            <AddressSearchField
              label="사업자 주소"
              required
              value={compAddress}
              error={compAddressErr}
              onSelect={(r: AddressSelectResult) => {
                setCompAddress(r.roadAddress);
                setCompGpsLat(r.gpsLat);
                setCompGpsLng(r.gpsLng);
                if (!r.roadAddress) setCompAddressDtl('');
                setCompAddressTouched(true);
              }}
              placeholder="도로명, 건물명으로 검색"
            />
          </div>

          {/* 상세 주소 — 주소 선택 후 표시 */}
          {compAddress && (
            <div style={{ marginBottom: 14 }}>
              <input
                type="text"
                placeholder="상세 주소 (동, 호수 등)"
                value={compAddressDtl}
                onChange={(e) => setCompAddressDtl(e.target.value)}
                className={INPUT_CLASS}
                style={inpStyle()}
                onFocus={(e) => focusInp(e.target)}
                onBlur={(e) => blurInp(e.target)}
              />
            </div>
          )}

          {/* 추천인 코드(선택) — C-8 */}
          <div style={{ marginBottom: 14 }}>
            <label className={LABEL_CLASS} style={labelStyle}>추천인 코드 (선택)</label>
            <input
              type="text" placeholder="추천인의 코드를 입력하세요" value={referralCode}
              onChange={(e) => { setReferralCode(e.target.value); setReferralMsg(null); }}
              className={INPUT_CLASS}
              style={inpStyle(!referralChecking && referralMsg && !referralMsg.ok ? { borderColor: 'var(--color-error)' } : {})}
              onFocus={(e) => focusInp(e.target)}
              onBlur={(e) => { blurInp(e.target); void checkReferralCode(referralCode); }}
            />
            {referralChecking && <p className="text-caption" style={{ marginTop: 5, color: 'var(--shell-text-muted)' }}>확인 중…</p>}
            {!referralChecking && referralMsg && (
              <p className="text-caption" style={{ marginTop: 5, color: referralMsg.ok ? 'var(--color-success)' : 'var(--color-error)' }}>
                {referralMsg.text}
              </p>
            )}
          </div>

          {/* 이용약관 */}
          <TermsConsentSection value={terms} onChange={setTerms} />
          {termsErr && <p className="text-caption" style={{ marginTop: -14, marginBottom: 16, color: 'var(--color-error)' }}>{termsErr}</p>}

          {/* 제출 에러 */}
          {submitError && (
            <div style={{ background: 'color-mix(in srgb, var(--color-error) 8%, transparent)', borderRadius: 12, padding: '10px 14px', marginBottom: 12 }}>
              <p className="text-caption font-medium" style={{ color: 'var(--color-error)' }}>{submitError}</p>
            </div>
          )}

          {/* 제출 버튼 */}
          <button
            type="button"
            onClick={() => { if (validateForm()) void handleSubmit(); }}
            disabled={isLoading}
            className="text-heading-sm font-bold"
            style={{
              width: '100%', padding: 15,
              background: !isLoading ? 'var(--gt-primary)' : 'var(--shell-border)',
              border: 'none', borderRadius: 14,
              color: !isLoading ? 'var(--gt-on-primary)' : 'var(--gray-400)',
              cursor: !isLoading ? 'pointer' : 'not-allowed',
              fontFamily: 'inherit',
              transition: 'filter .15s, transform .1s',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
            onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.filter = 'brightness(.92)'; }}
            onMouseLeave={(e) => { if (!isLoading) e.currentTarget.style.filter = 'none'; }}
            onMouseDown={(e) => { if (!isLoading) e.currentTarget.style.transform = 'scale(.98)'; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = ''; }}
          >
            {isLoading && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            )}
            {phoneHasDuplicate && otpVerified ? 'SNS 계정 연동하기' : '스튜디오 시작하기 →'}
          </button>

        </div>
      </div>
    </div>
  );
}
